import { ServiceItem, ServiceTransaction } from '../types';

/**
 * NEP — Eventualpositionen im Projekt-LV.
 *
 * Eine NEP-Position („Nur Einheitspreis") steht mit Preis im Angebot, ist
 * aber nicht beauftragt. Bei der Auftragsannahme kam sie bisher als ganz
 * normale Position in Leistung & Budget an — der Mitarbeiter konnte sie
 * nicht von einer beauftragten unterscheiden und buchte darauf wie auf jede
 * andere. Hier liegen die Regeln, die das aendern: die Erkennung
 * (ServiceItem.is_optional), die beauftragte Menge fuer Budget und
 * Fortschritt, die Pflicht-Begruendung einer Buchung
 * (ServiceTransaction.nepReason) und ihre Anzeige.
 *
 * Die Begruendung ist ein EIGENES Feld und wird nie in `description`
 * eingemischt — siehe ServiceTransaction.nepReason, warum. Wer sie zeigen
 * will, haengt sie mit `withNepReason` an den bereits fertigen Anzeigetext.
 */

/** Ist die Position eine Eventualposition (NEP)? Siehe ServiceItem.is_optional. */
export const isNepService = (s: Pick<ServiceItem, 'is_optional'> | null | undefined): boolean =>
    s?.is_optional === true;

/** Kurzkennzeichen an der Position — ueberall dieselben drei Buchstaben. */
export const NEP_BADGE = 'NEP';

/** Der Hinweis dazu, wortgleich in Hub, Details und Buchungsdialog. */
export const NEP_HINT = 'Eventualposition – nicht beauftragt';

/**
 * Die BEAUFTRAGTE Menge einer Position fuer Auftragswert, Soll und
 * Fortschritt.
 *
 * Betreiber 18.09.2026: „NEP rausrechnen und nur bei Buchung betrachten."
 * Eine NEP-Position ist nicht beauftragt — ihre Angebotsmenge ist ein
 * Vordersatz, kein Auftrag. Beauftragt ist, was gebucht (= mit Begruendung
 * nachbeauftragt) wurde: bis zur ersten Buchung 0, danach die gebuchte
 * Menge. So waechst der Auftragswert genau um die Nachbeauftragung, kein
 * Fortschritt haengt an einer Menge, die nie jemand bestellt hat, und ein
 * Projekt bleibt nicht wegen unbestellter Eventualmengen „noch offen"
 * (projectAutoArchive).
 *
 * NICHT fuer die Buchungspfade (isDone / status 'done' in reportBooking und
 * Geschwistern): dort bleibt der Vordersatz die Obergrenze, an der die
 * Position als voll gilt.
 */
export const contractedQuantity = (
    s: Pick<ServiceItem, 'is_optional' | 'quantityContracted' | 'quantityUsed'>,
): number => (isNepService(s) ? (s.quantityUsed || 0) : (s.quantityContracted || 0));

/**
 * Dieselbe Regel fuer die rohe `project_services`-Zeile (snake_case), wie
 * Dashboard, Projektkarte, Kanban und Cockpit sie aus der Listenzeile lesen.
 */
export const contractedQuantityOfRow = (
    row: { is_optional?: unknown; quantity_contracted?: unknown; quantity_used?: unknown } | null | undefined,
): number =>
    row?.is_optional === true
        ? (Number(row.quantity_used) || 0)
        : (Number(row?.quantity_contracted) || 0);

/**
 * Fuer die Wahl der TURNUSPOSITION (Projektkarte, Dashboard-Gantt):
 * Regelpositionen zuerst, NEP nur, wenn es keine Regelposition gibt.
 *
 * Ohne diese Vorauswahl gewann eine NEP mit vielen Buchungen (beauftragt =
 * gebucht, also z. B. 11/11) die Turnusrolle gegen eine Regelposition mit
 * 2/10 — und die Karte zeigte 100 %, obwohl acht Begehungen ausstehen
 * (Codex-Befund 18.09.2026).
 */
export const preferNonNep = <T extends { is_optional?: unknown }>(rows: T[]): T[] => {
    const regular = rows.filter((r) => r.is_optional !== true);
    return regular.length > 0 ? regular : rows;
};

/** Praefix, unter dem die Begruendung angezeigt wird. */
export const NEP_REASON_PREFIX = 'NEP-Begründung:';

/** Reicht die Begruendung? Leer oder nur Leerraum reicht nicht. */
export const isNepReasonGiven = (reason: string | null | undefined): boolean =>
    String(reason ?? '').trim().length > 0;

/** Die Begruendung als Anzeigezeile, oder null, wenn keine da ist. */
export const nepReasonLabel = (reason: string | null | undefined): string | null =>
    isNepReasonGiven(reason) ? `${NEP_REASON_PREFIX} ${String(reason).trim()}` : null;

/**
 * Anzeigetext plus Begruendung. Der Text kommt FERTIG hier an — nach jeder
 * Umschreibung (Nachweis: „Bericht Nr. 7" oder Positionstitel) — und die
 * Begruendung wird nur angehaengt, nie vorangestellt: so bleibt sie auch
 * dort stehen, wo der Nachweis die Beschreibung ersetzt, und veraendert
 * keine Regel, die auf dem Textanfang liest.
 */
export const withNepReason = (
    text: string,
    reason: Pick<ServiceTransaction, 'nepReason'>['nepReason'],
): string => {
    const label = nepReasonLabel(reason);
    if (!label) return text;
    return text.trim() ? `${text.trim()} · ${label}` : label;
};
